import React, { useState, useEffect } from 'react';
import { baseUrl } from '../../shared';
import dayjs from 'dayjs';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';

import { EquipmentSelector } from './CalibrationShared';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

const EMPTY_MDG41 = {
  type: 'HOSE_MDG41',
  technician: '', calibrationDate: '', calDueDate: '',
  temperature: '', humidity: '', comments: '',
  ratedPressure: '', testPressure: '', holdTime: '',
  endFitting1: '', endFitting2: '', hoseLength: '',
  hydraulicTemp: '', result: '',
};

const EMPTY_JACK = {
  type: 'HOSE_JACK',
  technician: '', calibrationDate: '', calDueDate: '',
  temperature: '', humidity: '', hydOilTemp: '', comments: '',
  testingProcedure: '',
  mainReliefPsi: '', hydraulicPsiA: '', hydraulicPsiB: '',
  maxOutputForceKgs: '', supplyAirPsi: '', supplyAirVolume: '',
  reinspectionDue: '', result: '',
};

export default function HoseTestCalForm({ open, onClose, job, cal, initialType, canManage, onSaved }) {
  const readOnly = !canManage;
  const defaultType = cal?.type || initialType || 'HOSE_MDG41';
  const [form, setForm] = useState(defaultType === 'HOSE_JACK' ? { ...EMPTY_JACK } : { ...EMPTY_MDG41 });
  const [equipmentUsed, setEquipmentUsed] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (cal) {
      const rd = cal.rawData || {};
      const baseForm = {
        type: cal.type || 'HOSE_MDG41',
        technician: cal.technician || '',
        calibrationDate: cal.calibrationDate ? dayjs(cal.calibrationDate).format('YYYY-MM-DD') : '',
        calDueDate: cal.calDueDate ? dayjs(cal.calDueDate).format('YYYY-MM-DD') : '',
        temperature: cal.temperature != null ? String(cal.temperature) : '',
        humidity: cal.humidity != null ? String(cal.humidity) : '',
        comments: cal.comments || '',
        result: cal.overallResult || '',
      };
      if (cal.type === 'HOSE_JACK') {
        setForm({ ...EMPTY_JACK, ...baseForm, hydOilTemp: rd.hydOilTemp || '', testingProcedure: rd.testingProcedure || '', mainReliefPsi: rd.mainReliefPsi || '', hydraulicPsiA: rd.hydraulicPsiA || '', hydraulicPsiB: rd.hydraulicPsiB || '', maxOutputForceKgs: rd.maxOutputForceKgs || '', supplyAirPsi: rd.supplyAirPsi || '', supplyAirVolume: rd.supplyAirVolume || '', reinspectionDue: rd.reinspectionDue || '' });
      } else {
        setForm({ ...EMPTY_MDG41, ...baseForm, ratedPressure: rd.ratedPressure || '', testPressure: rd.testPressure || '', holdTime: rd.holdTime || '', endFitting1: rd.endFitting1 || '', endFitting2: rd.endFitting2 || '', hoseLength: rd.hoseLength || '', hydraulicTemp: rd.hydraulicTemp || '' });
      }
      setEquipmentUsed(rd.equipmentSnapshot || []);
    } else {
      setForm(defaultType === 'HOSE_JACK' ? { ...EMPTY_JACK } : { ...EMPTY_MDG41 });
      setEquipmentUsed([]);
    }
  }, [open, cal]);

  useEffect(() => {
    if (form.calibrationDate && !cal?.calDueDate) {
      setForm(f => ({ ...f, calDueDate: dayjs(form.calibrationDate).add(12, 'month').format('YYYY-MM-DD') }));
    }
  }, [form.calibrationDate]);

  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));
  const isJack = form.type === 'HOSE_JACK';
  const typeLabel = isJack ? 'Hose/Jack Test' : 'Hydraulic Hose Test (MDG41)';
  const resultValid = form.result === 'PASS' || form.result === 'FAIL';

  const handleSave = async () => {
    if (equipmentUsed.length === 0) {
      alert('Please select the test equipment used before saving.');
      return;
    }
    setSaving(true);
    try {
      let rawData;
      if (isJack) {
        rawData = { hydOilTemp: form.hydOilTemp, testingProcedure: form.testingProcedure, mainReliefPsi: form.mainReliefPsi, hydraulicPsiA: form.hydraulicPsiA, hydraulicPsiB: form.hydraulicPsiB, maxOutputForceKgs: form.maxOutputForceKgs, supplyAirPsi: form.supplyAirPsi, supplyAirVolume: form.supplyAirVolume, reinspectionDue: form.reinspectionDue, equipmentSnapshot: equipmentUsed };
      } else {
        rawData = { ratedPressure: form.ratedPressure, testPressure: form.testPressure, holdTime: form.holdTime, endFitting1: form.endFitting1, endFitting2: form.endFitting2, hoseLength: form.hoseLength, hydraulicTemp: form.hydraulicTemp, equipmentSnapshot: equipmentUsed };
      }
      const body = {
        type: form.type,
        technician: form.technician,
        calibrationDate: form.calibrationDate,
        calDueDate: form.calDueDate,
        temperature: form.temperature,
        humidity: form.humidity,
        comments: form.comments,
        overallResult: resultValid ? form.result : null,
        rawData,
      };
      const url = cal
        ? baseUrl + 'workshop/jobs/' + job.id + '/calibrations/' + cal.id
        : baseUrl + 'workshop/jobs/' + job.id + '/calibrations';
      const res = await fetch(url, {
        method: cal ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) onSaved();
    } finally { setSaving(false); }
  };

  const resultChip = resultValid
    ? <Chip label={form.result} sx={{ bgcolor: form.result === 'PASS' ? 'var(--pass-bg)' : 'var(--fail-bg)', color: form.result === 'PASS' ? 'var(--pass)' : 'var(--fail)', fontWeight: 700, fontSize: 14 }} />
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
            {cal ? `${typeLabel} — ${cal.certNo || 'Draft'}` : `New ${typeLabel}`}
          </Typography>
          {resultChip}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}><Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Calibration Details</Typography><Divider sx={{ mt: 0.5, mb: 1.5 }} /></Grid>

          {!cal && (
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={form.type} label="Type" onChange={set('type')}>
                  <MenuItem value="HOSE_MDG41">Hydraulic Hose (MDG41)</MenuItem>
                  <MenuItem value="HOSE_JACK">Hose/Jack Test</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Technician" value={form.technician} onChange={set('technician')} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Calibration Date" type="date" value={form.calibrationDate} onChange={set('calibrationDate')} InputLabelProps={{ shrink: true }} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Cal Due Date" type="date" value={form.calDueDate} onChange={set('calDueDate')} InputLabelProps={{ shrink: true }} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Temperature (°C)" type="number" value={form.temperature} onChange={set('temperature')} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Humidity (%)" type="number" value={form.humidity} onChange={set('humidity')} InputProps={{ readOnly }} /></Grid>
          {isJack && (
            <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Hydraulic Oil Temp (°C)" type="number" value={form.hydOilTemp} onChange={set('hydOilTemp')} InputProps={{ readOnly }} /></Grid>
          )}
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small" disabled={readOnly}>
              <InputLabel>Result</InputLabel>
              <Select value={form.result} label="Result" onChange={set('result')}>
                <MenuItem value=""><em>Pending</em></MenuItem>
                <MenuItem value="PASS">PASS</MenuItem>
                <MenuItem value="FAIL">FAIL</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Comments" value={form.comments} onChange={set('comments')} InputProps={{ readOnly }} /></Grid>

          {/* MDG41-specific fields */}
          {!isJack && (
            <>
              <Grid item xs={12}><Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Hose Details</Typography><Divider sx={{ mt: 0.5, mb: 1.5 }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Rated Pressure (bar)" type="number" value={form.ratedPressure} onChange={set('ratedPressure')} InputProps={{ readOnly }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Test Pressure (bar)" type="number" value={form.testPressure} onChange={set('testPressure')} InputProps={{ readOnly }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Hold Time (mins)" type="number" value={form.holdTime} onChange={set('holdTime')} InputProps={{ readOnly }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="End Fitting 1" value={form.endFitting1} onChange={set('endFitting1')} InputProps={{ readOnly }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="End Fitting 2" value={form.endFitting2} onChange={set('endFitting2')} InputProps={{ readOnly }} /></Grid>
              <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Hose Length (m)" type="number" value={form.hoseLength} onChange={set('hoseLength')} InputProps={{ readOnly }} /></Grid>
              <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Hydraulic Temp (°C)" type="number" value={form.hydraulicTemp} onChange={set('hydraulicTemp')} InputProps={{ readOnly }} /></Grid>
            </>
          )}

          {/* Jack-specific fields */}
          {isJack && (
            <>
              <Grid item xs={12}><Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Test Details</Typography><Divider sx={{ mt: 0.5, mb: 1.5 }} /></Grid>
              <Grid item xs={12}><TextField size="small" fullWidth multiline rows={2} label="Testing Procedure" value={form.testingProcedure} onChange={set('testingProcedure')} InputProps={{ readOnly }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Main Relief PSI" type="number" value={form.mainReliefPsi} onChange={set('mainReliefPsi')} InputProps={{ readOnly }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Hydraulic PSI (A)" type="number" value={form.hydraulicPsiA} onChange={set('hydraulicPsiA')} InputProps={{ readOnly }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Hydraulic PSI (B)" type="number" value={form.hydraulicPsiB} onChange={set('hydraulicPsiB')} InputProps={{ readOnly }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Max Output Force (kgs)" type="number" value={form.maxOutputForceKgs} onChange={set('maxOutputForceKgs')} InputProps={{ readOnly }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Supply Air PSI" type="number" value={form.supplyAirPsi} onChange={set('supplyAirPsi')} InputProps={{ readOnly }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Supply Air Volume" value={form.supplyAirVolume} onChange={set('supplyAirVolume')} InputProps={{ readOnly }} /></Grid>
              <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Reinspection Due (months)" type="number" value={form.reinspectionDue} onChange={set('reinspectionDue')} InputProps={{ readOnly }} /></Grid>
            </>
          )}

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Test Equipment Used</Typography>
            <Divider sx={{ mt: 0.5, mb: 1.5 }} />
            <EquipmentSelector selected={equipmentUsed} onChange={setEquipmentUsed} readOnly={readOnly} />
          </Grid>

          {resultValid && (
            <Grid item xs={12}>
              <Box sx={{ mt: 1, p: 2, borderRadius: 1, textAlign: 'center', bgcolor: form.result === 'PASS' ? 'var(--pass-bg)' : 'var(--fail-bg)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: form.result === 'PASS' ? 'var(--pass)' : 'var(--fail)' }}>OVERALL RESULT: {form.result}</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Close</Button>
        {canManage && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : cal ? 'Save Changes' : 'Save Calibration'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
